import React, { FC } from 'react'
import { Link, Typography } from '@mui/material'
import { Formik } from 'formik'
import { IFormProps } from 'utils'
import {
  Module,
  ModuleHeader,
  ModuleBody,
  CustomAlert,
  CustomTextField,
  PasswordComplexityRequirement,
  ButtonContainer,
  CustomButton,
} from 'components'
import { sanitize } from 'dompurify'

interface IAdditionalProps extends IFormProps {
  passwordPolicy: any
  handleCancel: (e: any) => void
  handleSkip: (e: any) => void
  isPasswordExpiring: boolean
}

const PasswordExpiredForm: FC<IAdditionalProps> = ({
  isModuleInitialized,
  passwordPolicy,
  initialValues,
  validationSchema,
  handleSubmit,
  handleCancel,
  handleSkip,
  isPasswordExpiring,
  serviceError,
}) => (
  <Module isModuleInitialized={isModuleInitialized} serviceError={serviceError}>
    <ModuleHeader
      title={
        isModuleInitialized
          ? isPasswordExpiring
            ? 'Password is Expiring'
            : 'Password Expired'
          : ''
      }
    >
      {isModuleInitialized && (
        <Typography
          dangerouslySetInnerHTML={
            isPasswordExpiring
              ? {
                  __html: sanitize(
                    'You password is about to expire. Please update below.',
                  ),
                }
              : {
                  __html: sanitize(
                    'Your password has expired. Please update to log in.',
                  ),
                }
          }
        />
      )}
      <CustomAlert severity="error">{serviceError}</CustomAlert>
    </ModuleHeader>

    <ModuleBody>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          handleSubmit: onSubmit,
          dirty,
          isValid,
          isSubmitting,
          getFieldProps,
          getFieldMeta,
          setFieldTouched,
          setFieldError,
          touched,
          errors,
          values,
        }) => (
          <form onSubmit={onSubmit}>
            <CustomTextField
              id="oldPassword"
              type="password"
              label="Old Password"
              hasServiceError={Boolean(serviceError)}
              touched={touched}
              errors={errors}
              values={values}
              getFieldMeta={getFieldMeta}
              setFieldTouched={setFieldTouched}
              setFieldError={setFieldError}
              {...getFieldProps('oldPassword')}
            />

            <CustomTextField
              autoFocus
              id="newPassword"
              type="password"
              label="Enter new password"
              hasServiceError={Boolean(serviceError)}
              touched={touched}
              errors={errors}
              values={values}
              getFieldMeta={getFieldMeta}
              setFieldTouched={setFieldTouched}
              setFieldError={setFieldError}
              {...getFieldProps('newPassword')}
            />

            <PasswordComplexityRequirement
              passwordPolicyComplexity={passwordPolicy?.complexity}
              newPassword={values.newPassword}
            />

            <CustomTextField
              id="repeatPassword"
              type="password"
              label="Confirm new password"
              hasServiceError={Boolean(serviceError)}
              touched={touched}
              errors={errors}
              values={values}
              getFieldMeta={getFieldMeta}
              setFieldTouched={setFieldTouched}
              setFieldError={setFieldError}
              {...getFieldProps('repeatPassword')}
            />

            <ButtonContainer
              primary={
                <CustomButton
                  type="submit"
                  disabled={!dirty || !isValid || isSubmitting}
                >
                  Change Password
                </CustomButton>
              }
              middle={
                isPasswordExpiring ? (
                  <Link href="#" onClick={handleSkip} color="primary">
                    Skip
                  </Link>
                ) : undefined
              }
              secondary={
                <Link href="#" onClick={handleCancel} color="primary">
                  Return to Login
                </Link>
              }
            />
          </form>
        )}
      </Formik>
    </ModuleBody>
  </Module>
)

export default PasswordExpiredForm
